/**
 * Labstep
 *
 * @module constants/roles
 * @desc Organization custom permissions
 */

import type { PartialPermissionRoleSetting } from 'labstep-web/models/permission-role-setting.model';

interface IPermissionLabels {
  [key: string]: string;
}

export const PERMISSION_LABELS: IPermissionLabels = {
  view: 'Can View',
  create: 'Can Create',
  edit: 'Can Edit',
  delete: 'Can Delete',
  restore: 'Can Restore',
  lock: 'Can Lock',
  unlock: 'Can Unlock',
  assign: 'Can Assign',
  share: 'Can Share',
  comment: 'Can Comment',
  sign: 'Can Sign',
  create_items: 'Create Items',
  book: 'Can Book',
  send_data: 'Can Send Data',
  folder: 'Add to /Remove from Collection',
  tag: 'Add/Remove Tag',
  jupyter_notebook: 'Create/Edit Jupyter Notebook',
  jupyter_instance: 'Run Jupyter Notebook',
  create_new_version: 'Can draft new versions',
  draft_set_live: 'Can set drafts live',
  entity_import: 'Can import data',
};

export interface IPermissionRoleSettingsPostParameters {
  [key: string]: {
    [key: string]: {
      entity_name: string;
      action: string;
    };
  };
}

export const PermissionRoleSettingsPostParameters: IPermissionRoleSettingsPostParameters =
  {
    experiment_workflow: {
      view: {
        entity_name: 'experiment_workflow',
        action: 'view',
      },
      create: {
        entity_name: 'group',
        action: 'experiment_workflow:create',
      },
      edit: {
        entity_name: 'experiment_workflow',
        action: 'edit',
      },
      assign: {
        entity_name: 'experiment_workflow',
        action: 'entity_user:*',
      },
      delete: {
        entity_name: 'experiment_workflow',
        action: 'delete',
      },
      share: {
        entity_name: 'experiment_workflow',
        action: 'permission:*',
      },
      lock: {
        entity_name: 'experiment_workflow',
        action: 'lock',
      },
      unlock: {
        entity_name: 'experiment_workflow',
        action: 'unlock',
      },
      comment: {
        entity_name: 'experiment_workflow',
        action: 'comment:*',
      },
      sign: {
        entity_name: 'experiment_workflow',
        action: 'signature:*',
      },
      folder: {
        entity_name: 'experiment_workflow',
        action: 'folder_experiment_workflow:add_remove',
      },
      tag: {
        entity_name: 'experiment_workflow',
        action: 'tag:add_remove',
      },
      jupyter_notebook: {
        entity_name: 'experiment_workflow',
        action: 'jupyter_notebook:*',
      },
      jupyter_instance: {
        entity_name: 'experiment_workflow',
        action: 'jupyter_instance:*',
      },
    },
    folder_experiment_workflow: {
      view: {
        entity_name: 'folder_experiment_workflow',
        action: 'view',
      },
      create: {
        entity_name: 'group',
        action: 'folder_experiment_workflow:create',
      },
      edit: {
        entity_name: 'folder_experiment_workflow',
        action: 'edit',
      },
      delete: {
        entity_name: 'folder_experiment_workflow',
        action: 'delete',
      },
      assign: {
        entity_name: 'folder_experiment_workflow',
        action: 'entity_user:*',
      },
    },
    experiment_workflow_template: {
      view: {
        entity_name: 'experiment_workflow_template',
        action: 'view',
      },
      create: {
        entity_name: 'group',
        action: 'experiment_workflow_template:create',
      },
      edit: {
        entity_name: 'experiment_workflow_template',
        action: 'edit',
      },
      assign: {
        entity_name: 'experiment_workflow_template',
        action: 'entity_user:*',
      },
      delete: {
        entity_name: 'experiment_workflow_template',
        action: 'delete',
      },
      share: {
        entity_name: 'experiment_workflow_template',
        action: 'permission:*',
      },
      lock: {
        entity_name: 'experiment_workflow_template',
        action: 'lock',
      },
      unlock: {
        entity_name: 'experiment_workflow_template',
        action: 'unlock',
      },
      comment: {
        entity_name: 'experiment_workflow_template',
        action: 'comment:*',
      },
      jupyter_notebook: {
        entity_name: 'experiment_workflow_template',
        action: 'jupyter_notebook:*',
      },
      jupyter_instance: {
        entity_name: 'experiment_workflow_template',
        action: 'jupyter_instance:*',
      },
    },
    protocol_collection: {
      view: {
        entity_name: 'protocol_collection',
        action: 'view',
      },
      create: {
        entity_name: 'group',
        action: 'protocol_collection:create',
      },
      create_new_version: {
        entity_name: 'protocol_collection',
        action: 'edit',
      },
      draft_set_live: {
        entity_name: 'protocol_collection',
        action: 'protocol:set_live',
      },
      assign: {
        entity_name: 'protocol_collection',
        action: 'entity_user:*',
      },
      delete: {
        entity_name: 'protocol_collection',
        action: 'delete',
      },
      share: {
        entity_name: 'protocol_collection',
        action: 'permission:*',
      },
      comment: {
        entity_name: 'protocol_collection',
        action: 'comment:*',
      },
      folder: {
        entity_name: 'protocol_collection',
        action: 'folder_protocol_collection:add_remove',
      },
      tag: {
        entity_name: 'protocol_collection',
        action: 'tag:add_remove',
      },
      jupyter_notebook: {
        entity_name: 'protocol_collection',
        action: 'jupyter_notebook:*',
      },
      jupyter_instance: {
        entity_name: 'protocol_collection',
        action: 'jupyter_instance:*',
      },
    },
    folder_protocol_collection: {
      view: {
        entity_name: 'folder_protocol_collection',
        action: 'view',
      },
      create: {
        entity_name: 'group',
        action: 'folder_protocol_collection:create',
      },
      edit: {
        entity_name: 'folder_protocol_collection',
        action: 'edit',
      },
      delete: {
        entity_name: 'folder_protocol_collection',
        action: 'delete',
      },
      assign: {
        entity_name: 'folder_protocol_collection',
        action: 'entity_user:*',
      },
    },
    resource_template: {
      create: {
        entity_name: 'group',
        action: 'resource_template:create',
      },
      edit: {
        entity_name: 'resource_template',
        action: 'edit',
      },
      delete: {
        entity_name: 'resource_template',
        action: 'delete',
      },
      comment: {
        entity_name: 'resource_template',
        action: 'comment:*',
      },
      share: {
        entity_name: 'resource_template',
        action: 'permission:*',
      },
    },
    resource: {
      create: {
        entity_name: 'group',
        action: 'resource:create',
      },
      edit: {
        entity_name: 'resource',
        action: 'edit',
      },
      assign: {
        entity_name: 'resource',
        action: 'entity_user:*',
      },
      delete: {
        entity_name: 'resource',
        action: 'delete',
      },
      share: {
        entity_name: 'resource',
        action: 'permission:*',
      },
      comment: {
        entity_name: 'resource',
        action: 'comment:*',
      },
      tag: {
        entity_name: 'resource',
        action: 'tag:add_remove',
      },
      create_items: {
        entity_name: 'resource',
        action: 'resource_item:create',
      },
    },
    resource_location: {
      create: {
        entity_name: 'group',
        action: 'resource_location:create',
      },
      edit: {
        entity_name: 'resource_location',
        action: 'edit',
      },
      assign: {
        entity_name: 'resource_location',
        action: 'entity_user:*',
      },
      delete: {
        entity_name: 'resource_location',
        action: 'delete',
      },
      comment: {
        entity_name: 'resource_location',
        action: 'comment:*',
      },
    },
    order_request: {
      create: {
        entity_name: 'group',
        action: 'order_request:create',
      },
      edit: {
        entity_name: 'order_request',
        action: 'edit',
      },
      assign: {
        entity_name: 'order_request',
        action: 'entity_user:*',
      },
      delete: {
        entity_name: 'order_request',
        action: 'delete',
      },
      share: {
        entity_name: 'order_request',
        action: 'permission:*',
      },
      comment: {
        entity_name: 'order_request',
        action: 'comment:*',
      },
      tag: {
        entity_name: 'order_request',
        action: 'tag:add_remove',
      },
    },
    purchase_order: {
      create: {
        entity_name: 'group',
        action: 'purchase_order:create',
      },
      edit: {
        entity_name: 'purchase_order',
        action: 'edit',
      },
      assign: {
        entity_name: 'purchase_order',
        action: 'entity_user:*',
      },
      delete: {
        entity_name: 'purchase_order',
        action: 'delete',
      },
      comment: {
        entity_name: 'purchase_order',
        action: 'comment:*',
      },
      tag: {
        entity_name: 'purchase_order',
        action: 'tag:add_remove',
      },
    },
    device_template: {
      create: {
        entity_name: 'group',
        action: 'device_template:create',
      },
      edit: {
        entity_name: 'device_template',
        action: 'edit',
      },
      delete: {
        entity_name: 'device_template',
        action: 'delete',
      },
      comment: {
        entity_name: 'device_template',
        action: 'comment:*',
      },
      share: {
        entity_name: 'device_template',
        action: 'permission:*',
      },
    },
    device: {
      create: {
        entity_name: 'group',
        action: 'device:create',
      },
      edit: {
        entity_name: 'device',
        action: 'edit',
      },
      assign: {
        entity_name: 'device',
        action: 'entity_user:*',
      },
      delete: {
        entity_name: 'device',
        action: 'delete',
      },
      share: {
        entity_name: 'device',
        action: 'permission:*',
      },
      comment: {
        entity_name: 'device',
        action: 'comment:*',
      },
      send_data: {
        entity_name: 'device',
        action: 'device_data:*',
      },
      book: {
        entity_name: 'device',
        action: 'device_booking:*',
      },
      tag: {
        entity_name: 'device',
        action: 'tag:add_remove',
      },
    },
    tag: {
      create: {
        entity_name: 'group',
        action: 'tag:create',
      },
      edit: {
        entity_name: 'tag',
        action: 'edit',
      },
      delete: {
        entity_name: 'tag',
        action: 'delete',
      },
    },
    group: {
      comment: {
        entity_name: 'group',
        action: 'comment:*',
      },
      entity_import: {
        entity_name: 'group',
        action: 'entity_import:*',
      },
    },
    entity_state_workflow: {
      create: {
        entity_name: 'group',
        action: 'entity_state_workflow:create',
      },
      edit: {
        entity_name: 'entity_state_workflow',
        action: 'edit',
      },
      assign: {
        entity_name: 'entity_state_workflow',
        action: 'entity_user:*',
      },
      delete: {
        entity_name: 'entity_state_workflow',
        action: 'delete',
      },
      share: {
        entity_name: 'entity_state_workflow',
        action: 'permission:*',
      },
    },
  };

export interface IPermissionRoleSections {
  [key: string]: {
    [key: string]: string[];
  };
}

export const PermissionRoleSections: IPermissionRoleSections = {
  experiment_workflow: {
    view: ['all', 'assigned', 'none'],
    create: ['all', 'none'],
    edit: ['all', 'assigned', 'none'],
    assign: ['all', 'assigned', 'none'],
    delete: ['all', 'assigned', 'none'],
    share: ['all', 'assigned', 'none'],
    lock: ['all', 'assigned', 'none'],
    unlock: ['all', 'assigned', 'none'],
    comment: ['all', 'assigned', 'none'],
    sign: ['all', 'assigned', 'none'],
    folder: ['all', 'assigned', 'none'],
    tag: ['all', 'assigned', 'none'],
    jupyter_notebook: ['all', 'assigned', 'none'],
    jupyter_instance: ['all', 'assigned', 'none'],
  },
  folder_experiment_workflow: {
    view: ['all', 'assigned', 'none'],
    create: ['all', 'none'],
    edit: ['all', 'none'],
    delete: ['all', 'none'],
    assign: ['all', 'none'],
  },
  experiment_workflow_template: {
    view: ['all', 'assigned', 'none'],
    create: ['all', 'none'],
    edit: ['all', 'assigned', 'none'],
    assign: ['all', 'assigned', 'none'],
    delete: ['all', 'assigned', 'none'],
    share: ['all', 'assigned', 'none'],
    lock: ['all', 'assigned', 'none'],
    unlock: ['all', 'assigned', 'none'],
    comment: ['all', 'assigned', 'none'],
    jupyter_notebook: ['all', 'assigned', 'none'],
    jupyter_instance: ['all', 'assigned', 'none'],
  },
  protocol_collection: {
    view: ['all', 'assigned', 'none'],
    create: ['all', 'none'],
    create_new_version: ['all', 'assigned', 'none'],
    draft_set_live: ['all', 'assigned', 'none'],
    assign: ['all', 'assigned', 'none'],
    delete: ['all', 'assigned', 'none'],
    share: ['all', 'assigned', 'none'],
    comment: ['all', 'assigned', 'none'],
    folder: ['all', 'assigned', 'none'],
    tag: ['all', 'assigned', 'none'],
    jupyter_notebook: ['all', 'assigned', 'none'],
    jupyter_instance: ['all', 'assigned', 'none'],
  },
  folder_protocol_collection: {
    view: ['all', 'assigned', 'none'],
    create: ['all', 'none'],
    edit: ['all', 'none'],
    delete: ['all', 'none'],
    assign: ['all', 'none'],
  },
  resource_template: {
    create: ['all', 'none'],
    edit: ['all', 'assigned', 'none'],
    delete: ['all', 'assigned', 'none'],
    comment: ['all', 'assigned', 'none'],
    share: ['all', 'assigned', 'none'],
  },
  resource: {
    create: ['all', 'none'],
    edit: ['all', 'assigned', 'none'],
    assign: ['all', 'assigned', 'none'],
    delete: ['all', 'assigned', 'none'],
    share: ['all', 'assigned', 'none'],
    comment: ['all', 'assigned', 'none'],
    tag: ['all', 'assigned', 'none'],
    create_items: ['all', 'assigned', 'none'],
  },
  resource_location: {
    create: ['all', 'none'],
    edit: ['all', 'assigned', 'none'],
    assign: ['all', 'assigned', 'none'],
    delete: ['all', 'assigned', 'none'],
    comment: ['all', 'assigned', 'none'],
  },
  order_request: {
    create: ['all', 'none'],
    edit: ['all', 'assigned', 'none'],
    assign: ['all', 'assigned', 'none'],
    delete: ['all', 'assigned', 'none'],
    share: ['all', 'assigned', 'none'],
    comment: ['all', 'assigned', 'none'],
    tag: ['all', 'assigned', 'none'],
  },
  purchase_order: {
    create: ['all', 'none'],
    edit: ['all', 'assigned', 'none'],
    assign: ['all', 'assigned', 'none'],
    delete: ['all', 'assigned', 'none'],
    comment: ['all', 'assigned', 'none'],
    tag: ['all', 'assigned', 'none'],
  },
  device_template: {
    create: ['all', 'none'],
    edit: ['all', 'assigned', 'none'],
    delete: ['all', 'assigned', 'none'],
    comment: ['all', 'assigned', 'none'],
    share: ['all', 'assigned', 'none'],
  },
  device: {
    create: ['all', 'none'],
    edit: ['all', 'assigned', 'none'],
    assign: ['all', 'assigned', 'none'],
    delete: ['all', 'assigned', 'none'],
    share: ['all', 'assigned', 'none'],
    comment: ['all', 'assigned', 'none'],
    send_data: ['all', 'assigned', 'none'],
    book: ['all', 'assigned', 'none'],
    tag: ['all', 'assigned', 'none'],
  },
  tag: {
    create: ['all', 'none'],
    edit: ['all', 'none'],
    delete: ['all', 'none'],
  },
  group: {
    comment: ['all', 'none'],
    entity_import: ['all', 'none'],
  },
  entity_state_workflow: {
    create: ['all', 'none'],
    edit: ['all', 'none'],
    assign: ['all', 'none'],
    delete: ['all', 'none'],
    share: ['all', 'none'],
  },
};

export const PERMISSION_ROLE_OWNER = {
  guid: 'owner',
  name: 'Owner',
};

export const OWNER_PERMISSION_ROLE_SETTINGS: PartialPermissionRoleSetting[] =
  [
    // experiment_workflow
    {
      entity_name: 'experiment_workflow',
      action: 'view',
      type: 'all',
    },
    {
      entity_name: 'group',
      action: 'experiment_workflow:create',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow',
      action: 'edit',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow',
      action: 'entity_user:*',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow',
      action: 'delete',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow',
      action: 'permission:*',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow',
      action: 'lock',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow',
      action: 'unlock',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow',
      action: 'comment:*',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow',
      action: 'signature:*',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow',
      action: 'folder_experiment_workflow:add_remove',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow',
      action: 'tag:add_remove',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow',
      action: 'jupyter_notebook:*',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow',
      action: 'jupyter_instance:*',
      type: 'all',
    },

    // folder_experiment_workflow
    {
      entity_name: 'folder_experiment_workflow',
      action: 'view',
      type: 'all',
    },
    {
      entity_name: 'group',
      action: 'folder_experiment_workflow:create',
      type: 'all',
    },
    {
      entity_name: 'folder_experiment_workflow',
      action: 'edit',
      type: 'all',
    },
    {
      entity_name: 'folder_experiment_workflow',
      action: 'delete',
      type: 'all',
    },
    {
      entity_name: 'folder_experiment_workflow',
      action: 'entity_user:*',
      type: 'all',
    },

    // experiment_workflow_template
    {
      entity_name: 'group',
      action: 'experiment_workflow_template:view',
      type: 'all',
    },
    {
      entity_name: 'group',
      action: 'experiment_workflow_template:create',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow_template',
      action: 'edit',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow_template',
      action: 'entity_user:*',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow_template',
      action: 'delete',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow_template',
      action: 'permission:*',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow_template',
      action: 'lock',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow_template',
      action: 'unlock',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow_template',
      action: 'comment:*',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow_template',
      action: 'jupyter_notebook:*',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow_template',
      action: 'jupyter_instance:*',
      type: 'all',
    },

    // protocol_collection
    {
      entity_name: 'group',
      action: 'protocol_collection:view',
      type: 'all',
    },
    {
      entity_name: 'group',
      action: 'protocol_collection:create',
      type: 'all',
    },
    {
      entity_name: 'protocol_collection',
      action: 'edit',
      type: 'all',
    },
    {
      entity_name: 'protocol_collection',
      action: 'protocol:set_live',
      type: 'all',
    },
    {
      entity_name: 'protocol_collection',
      action: 'entity_user:*',
      type: 'all',
    },
    {
      entity_name: 'protocol_collection',
      action: 'delete',
      type: 'all',
    },
    {
      entity_name: 'protocol_collection',
      action: 'permission:*',
      type: 'all',
    },
    {
      entity_name: 'protocol_collection',
      action: 'comment:*',
      type: 'all',
    },
    {
      entity_name: 'protocol_collection',
      action: 'folder_protocol_collection:add_remove',
      type: 'all',
    },
    {
      entity_name: 'protocol_collection',
      action: 'tag:add_remove',
      type: 'all',
    },
    {
      entity_name: 'protocol_collection',
      action: 'jupyter_notebook:*',
      type: 'all',
    },
    {
      entity_name: 'protocol_collection',
      action: 'jupyter_instance:*',
      type: 'all',
    },

    // folder_protocol_collection
    {
      entity_name: 'folder_protocol_collection',
      action: 'view',
      type: 'all',
    },
    {
      entity_name: 'group',
      action: 'folder_protocol_collection:create',
      type: 'all',
    },
    {
      entity_name: 'folder_protocol_collection',
      action: 'edit',
      type: 'all',
    },
    {
      entity_name: 'folder_protocol_collection',
      action: 'delete',
      type: 'all',
    },
    {
      entity_name: 'folder_protocol_collection',
      action: 'entity_user:*',
      type: 'all',
    },

    // resource_template
    {
      entity_name: 'group',
      action: 'resource_template:create',
      type: 'all',
    },
    { entity_name: 'resource_template', action: 'edit', type: 'all' },
    {
      entity_name: 'resource_template',
      action: 'delete',
      type: 'all',
    },
    {
      entity_name: 'resource_template',
      action: 'comment:*',
      type: 'all',
    },
    {
      entity_name: 'resource_template',
      action: 'permission:*',
      type: 'all',
    },

    // resource
    { entity_name: 'group', action: 'resource:create', type: 'all' },
    { entity_name: 'resource', action: 'edit', type: 'all' },
    { entity_name: 'resource', action: 'entity_user:*', type: 'all' },
    { entity_name: 'resource', action: 'delete', type: 'all' },
    { entity_name: 'resource', action: 'permission:*', type: 'all' },
    { entity_name: 'resource', action: 'comment:*', type: 'all' },
    {
      entity_name: 'resource',
      action: 'tag:add_remove',
      type: 'all',
    },
    {
      entity_name: 'resource',
      action: 'resource_item:create',
      type: 'all',
    },

    // resource_location
    {
      entity_name: 'group',
      action: 'resource_location:create',
      type: 'all',
    },
    { entity_name: 'resource_location', action: 'edit', type: 'all' },
    {
      entity_name: 'resource_location',
      action: 'entity_user:*',
      type: 'all',
    },
    {
      entity_name: 'resource_location',
      action: 'delete',
      type: 'all',
    },
    {
      entity_name: 'resource_location',
      action: 'comment:*',
      type: 'all',
    },

    // order_request
    {
      entity_name: 'group',
      action: 'order_request:create',
      type: 'all',
    },
    { entity_name: 'order_request', action: 'edit', type: 'all' },
    {
      entity_name: 'order_request',
      action: 'entity_user:*',
      type: 'all',
    },
    { entity_name: 'order_request', action: 'delete', type: 'all' },
    {
      entity_name: 'order_request',
      action: 'permission:*',
      type: 'all',
    },
    {
      entity_name: 'order_request',
      action: 'comment:*',
      type: 'all',
    },
    {
      entity_name: 'order_request',
      action: 'tag:add_remove',
      type: 'all',
    },

    // purchase_order
    {
      entity_name: 'group',
      action: 'purchase_order:create',
      type: 'all',
    },
    { entity_name: 'purchase_order', action: 'edit', type: 'all' },
    {
      entity_name: 'purchase_order',
      action: 'entity_user:*',
      type: 'all',
    },
    { entity_name: 'purchase_order', action: 'delete', type: 'all' },
    {
      entity_name: 'purchase_order',
      action: 'comment:*',
      type: 'all',
    },
    {
      entity_name: 'purchase_order',
      action: 'tag:add_remove',
      type: 'all',
    },

    // device_template
    {
      entity_name: 'group',
      action: 'device_template:create',
      type: 'all',
    },
    { entity_name: 'device_template', action: 'edit', type: 'all' },
    { entity_name: 'device_template', action: 'delete', type: 'all' },
    {
      entity_name: 'device_template',
      action: 'comment:*',
      type: 'all',
    },
    {
      entity_name: 'device_template',
      action: 'permission:*',
      type: 'all',
    },

    // device
    { entity_name: 'group', action: 'device:create', type: 'all' },
    { entity_name: 'device', action: 'edit', type: 'all' },
    { entity_name: 'device', action: 'entity_user:*', type: 'all' },
    { entity_name: 'device', action: 'delete', type: 'all' },
    { entity_name: 'device', action: 'permission:*', type: 'all' },
    { entity_name: 'device', action: 'comment:*', type: 'all' },
    { entity_name: 'device', action: 'device_data:*', type: 'all' },
    {
      entity_name: 'device',
      action: 'device_booking:*',
      type: 'all',
    },
    { entity_name: 'device', action: 'tag:add_remove', type: 'all' },

    // tag
    { entity_name: 'group', action: 'tag:create', type: 'all' },
    { entity_name: 'tag', action: 'edit', type: 'all' },
    { entity_name: 'tag', action: 'delete', type: 'all' },

    // group
    { entity_name: 'group', action: 'comment:*', type: 'all' },
    { entity_name: 'group', action: 'entity_import:*', type: 'all' },

    // entity_state_workflow
    {
      entity_name: 'group',
      action: 'entity_state_workflow:create',
      type: 'all',
    },
    {
      entity_name: 'entity_state_workflow',
      action: 'edit',
      type: 'all',
    },
    {
      entity_name: 'entity_state_workflow',
      action: 'entity_user:*',
      type: 'all',
    },
    {
      entity_name: 'entity_state_workflow',
      action: 'delete',
      type: 'all',
    },
    {
      entity_name: 'entity_state_workflow',
      action: 'permission:*',
      type: 'all',
    },
  ];

export const PERMISSION_ROLE_EDITOR = {
  guid: 'editor',
  name: 'Editor',
};

export const EDITOR_PERMISSION_ROLE_SETTINGS: PartialPermissionRoleSetting[] =
  [
    // experiment_workflow
    {
      entity_name: 'experiment_workflow',
      action: 'view',
      type: 'all',
    },
    {
      entity_name: 'group',
      action: 'experiment_workflow:create',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow',
      action: 'edit',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow',
      action: 'entity_user:*',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow',
      action: 'delete',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow',
      action: 'permission:*',
      type: 'none',
    },
    {
      entity_name: 'experiment_workflow',
      action: 'lock',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow',
      action: 'unlock',
      type: 'none',
    },
    {
      entity_name: 'experiment_workflow',
      action: 'comment:*',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow',
      action: 'signature:*',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow',
      action: 'folder_experiment_workflow:add_remove',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow',
      action: 'tag:add_remove',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow',
      action: 'jupyter_notebook:*',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow',
      action: 'jupyter_instance:*',
      type: 'all',
    },

    // folder_experiment_workflow
    {
      entity_name: 'folder_experiment_workflow',
      action: 'view',
      type: 'all',
    },
    {
      entity_name: 'group',
      action: 'folder_experiment_workflow:create',
      type: 'all',
    },
    {
      entity_name: 'folder_experiment_workflow',
      action: 'edit',
      type: 'all',
    },
    {
      entity_name: 'folder_experiment_workflow',
      action: 'delete',
      type: 'all',
    },
    {
      entity_name: 'folder_experiment_workflow',
      action: 'entity_user:*',
      type: 'all',
    },

    // experiment_workflow_template
    {
      entity_name: 'group',
      action: 'experiment_workflow_template:view',
      type: 'all',
    },
    {
      entity_name: 'group',
      action: 'experiment_workflow_template:create',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow_template',
      action: 'edit',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow_template',
      action: 'entity_user:*',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow_template',
      action: 'delete',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow_template',
      action: 'permission:*',
      type: 'none',
    },
    {
      entity_name: 'experiment_workflow_template',
      action: 'lock',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow_template',
      action: 'unlock',
      type: 'none',
    },
    {
      entity_name: 'experiment_workflow_template',
      action: 'comment:*',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow_template',
      action: 'jupyter_notebook:*',
      type: 'all',
    },
    {
      entity_name: 'experiment_workflow_template',
      action: 'jupyter_instance:*',
      type: 'all',
    },

    // protocol_collection
    {
      entity_name: 'group',
      action: 'protocol_collection:view',
      type: 'all',
    },
    {
      entity_name: 'group',
      action: 'protocol_collection:create',
      type: 'all',
    },
    {
      entity_name: 'protocol_collection',
      action: 'edit',
      type: 'all',
    },
    {
      entity_name: 'protocol_collection',
      action: 'protocol:set_live',
      type: 'all',
    },
    {
      entity_name: 'protocol_collection',
      action: 'entity_user:*',
      type: 'all',
    },
    {
      entity_name: 'protocol_collection',
      action: 'delete',
      type: 'all',
    },
    {
      entity_name: 'protocol_collection',
      action: 'permission:*',
      type: 'none',
    },
    {
      entity_name: 'protocol_collection',
      action: 'comment:*',
      type: 'all',
    },
    {
      entity_name: 'protocol_collection',
      action: 'folder_protocol_collection:add_remove',
      type: 'all',
    },
    {
      entity_name: 'protocol_collection',
      action: 'tag:add_remove',
      type: 'all',
    },
    {
      entity_name: 'protocol_collection',
      action: 'jupyter_notebook:*',
      type: 'all',
    },
    {
      entity_name: 'protocol_collection',
      action: 'jupyter_instance:*',
      type: 'all',
    },

    // folder_protocol_collection
    {
      entity_name: 'folder_protocol_collection',
      action: 'view',
      type: 'all',
    },
    {
      entity_name: 'group',
      action: 'folder_protocol_collection:create',
      type: 'all',
    },
    {
      entity_name: 'folder_protocol_collection',
      action: 'edit',
      type: 'all',
    },
    {
      entity_name: 'folder_protocol_collection',
      action: 'delete',
      type: 'all',
    },
    {
      entity_name: 'folder_protocol_collection',
      action: 'entity_user:*',
      type: 'all',
    },

    // resource_template
    {
      entity_name: 'group',
      action: 'resource_template:create',
      type: 'all',
    },
    { entity_name: 'resource_template', action: 'edit', type: 'all' },
    {
      entity_name: 'resource_template',
      action: 'delete',
      type: 'all',
    },
    {
      entity_name: 'resource_template',
      action: 'comment:*',
      type: 'all',
    },
    {
      entity_name: 'resource_template',
      action: 'permission:*',
      type: 'none',
    },

    // resource
    { entity_name: 'group', action: 'resource:create', type: 'all' },
    { entity_name: 'resource', action: 'edit', type: 'all' },
    { entity_name: 'resource', action: 'entity_user:*', type: 'all' },
    { entity_name: 'resource', action: 'delete', type: 'all' },
    { entity_name: 'resource', action: 'permission:*', type: 'none' },
    { entity_name: 'resource', action: 'comment:*', type: 'all' },
    {
      entity_name: 'resource',
      action: 'tag:add_remove',
      type: 'all',
    },
    {
      entity_name: 'resource',
      action: 'resource_item:create',
      type: 'all',
    },

    // resource_location
    {
      entity_name: 'group',
      action: 'resource_location:create',
      type: 'all',
    },
    { entity_name: 'resource_location', action: 'edit', type: 'all' },
    {
      entity_name: 'resource_location',
      action: 'entity_user:*',
      type: 'all',
    },
    {
      entity_name: 'resource_location',
      action: 'delete',
      type: 'all',
    },
    {
      entity_name: 'resource_location',
      action: 'comment:*',
      type: 'all',
    },

    // order_request
    {
      entity_name: 'group',
      action: 'order_request:create',
      type: 'all',
    },
    { entity_name: 'order_request', action: 'edit', type: 'all' },
    {
      entity_name: 'order_request',
      action: 'entity_user:*',
      type: 'all',
    },
    { entity_name: 'order_request', action: 'delete', type: 'all' },
    {
      entity_name: 'order_request',
      action: 'permission:*',
      type: 'none',
    },
    {
      entity_name: 'order_request',
      action: 'comment:*',
      type: 'all',
    },
    {
      entity_name: 'order_request',
      action: 'tag:add_remove',
      type: 'all',
    },

    // purchase_order
    {
      entity_name: 'group',
      action: 'purchase_order:create',
      type: 'all',
    },
    { entity_name: 'purchase_order', action: 'edit', type: 'all' },
    {
      entity_name: 'purchase_order',
      action: 'entity_user:*',
      type: 'all',
    },
    { entity_name: 'purchase_order', action: 'delete', type: 'all' },
    {
      entity_name: 'purchase_order',
      action: 'comment:*',
      type: 'all',
    },
    {
      entity_name: 'purchase_order',
      action: 'tag:add_remove',
      type: 'all',
    },

    // device_template
    {
      entity_name: 'group',
      action: 'device_template:create',
      type: 'all',
    },
    { entity_name: 'device_template', action: 'edit', type: 'all' },
    { entity_name: 'device_template', action: 'delete', type: 'all' },
    {
      entity_name: 'device_template',
      action: 'comment:*',
      type: 'all',
    },
    {
      entity_name: 'device_template',
      action: 'permission:*',
      type: 'none',
    },

    // device
    { entity_name: 'group', action: 'device:create', type: 'all' },
    { entity_name: 'device', action: 'edit', type: 'all' },
    { entity_name: 'device', action: 'entity_user:*', type: 'all' },
    { entity_name: 'device', action: 'delete', type: 'all' },
    { entity_name: 'device', action: 'permission:*', type: 'none' },
    { entity_name: 'device', action: 'comment:*', type: 'all' },
    { entity_name: 'device', action: 'device_data:*', type: 'all' },
    {
      entity_name: 'device',
      action: 'device_booking:*',
      type: 'all',
    },
    { entity_name: 'device', action: 'tag:add_remove', type: 'all' },

    // tag
    { entity_name: 'group', action: 'tag:create', type: 'all' },
    { entity_name: 'tag', action: 'edit', type: 'all' },
    { entity_name: 'tag', action: 'delete', type: 'all' },

    // group
    { entity_name: 'group', action: 'comment:*', type: 'all' },
    { entity_name: 'group', action: 'entity_import:*', type: 'all' },

    // entity_state_workflow
    {
      entity_name: 'group',
      action: 'entity_state_workflow:create',
      type: 'none',
    },
    {
      entity_name: 'entity_state_workflow',
      action: 'edit',
      type: 'none',
    },
    {
      entity_name: 'entity_state_workflow',
      action: 'entity_user:*',
      type: 'none',
    },
    {
      entity_name: 'entity_state_workflow',
      action: 'delete',
      type: 'none',
    },
    {
      entity_name: 'entity_state_workflow',
      action: 'permission:*',
      type: 'none',
    },
  ];

export const PERMISSION_ROLE_VIEWER = {
  guid: 'viewer',
  name: 'Viewer',
};

interface PermissionRoleSettings {
  name: string;
  permission_role_settings: PartialPermissionRoleSetting[];
}

export const DEFAULT_PERMISSION_ROLE_SETTINGS: PermissionRoleSettings[] =
  [
    {
      name: 'Owner',
      permission_role_settings: OWNER_PERMISSION_ROLE_SETTINGS,
    },
    {
      name: 'Editor',
      permission_role_settings: EDITOR_PERMISSION_ROLE_SETTINGS,
    },
    {
      name: 'Viewer',
      permission_role_settings: [
        {
          entity_name: 'group',
          action: 'experiment_workflow:create',
          type: 'none',
        },
        {
          entity_name: 'experiment_workflow',
          action: 'view',
          type: 'all',
        },
        {
          entity_name: 'experiment_workflow_template',
          action: 'view',
          type: 'all',
        },
        {
          entity_name: 'protocol_collection',
          action: 'view',
          type: 'all',
        },
        {
          entity_name: 'folder_experiment_workflow',
          action: 'view',
          type: 'all',
        },
        {
          entity_name: 'folder_protocol_collection',
          action: 'view',
          type: 'all',
        },
      ],
    },
  ];

export const DEFAULT_PERMISSION_ROLES = [
  PERMISSION_ROLE_OWNER,
  PERMISSION_ROLE_EDITOR,
  PERMISSION_ROLE_VIEWER,
];
